import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Empty, Segmented, Spin, Pagination, Tooltip } from 'antd';
import { MessageOutlined, ReloadOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import discussionService from '../../services/discussionService';
import { CHARTE_COLORS as C } from '../../variables/constants';
import PageHeader from '../../components/PageHeader';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

/**
 * Vue transverse des conversations : tous les fils qu'un agent suit, au même endroit.
 *
 * POURQUOI CET ÉCRAN EXISTE.
 * Le rail latéral ne vit qu'à l'intérieur d'un dossier. Sans point d'entrée transverse,
 * un agent sollicité sur cinq dossiers dans la journée doit ouvrir cinq dossiers pour
 * savoir ce qu'on lui veut. C'est le trajet qui tue les outils de collaboration : ce
 * n'est pas d'écrire qui coûte, c'est d'aller voir.
 *
 * On est abonné à un fil dès qu'on y est nommé ou qu'on y prend la parole - jamais par
 * une case à cocher. Personne ne gère une liste d'abonnements à la main.
 */

const TAILLE_PAGE = 20;

const AVATARS = ['#1a6b3c', '#7c3aed', '#b45309', '#1d4ed8', '#be123c', '#0f766e'];
const couleurAvatar = (cle = '') => {
  let somme = 0;
  for (let i = 0; i < cle.length; i += 1) somme += cle.charCodeAt(i);
  return AVATARS[somme % AVATARS.length];
};

const tempsRelatif = (iso) => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return i18n.t('discussions:temps.instant');
  if (min < 60) return i18n.t('discussions:temps.minutes', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t('discussions:temps.heures', { n: h });
  const j = Math.floor(h / 24);
  if (j === 1) return i18n.t('discussions:temps.hier');
  if (j < 31) return i18n.t('discussions:temps.jours', { n: j });
  return new Date(iso).toLocaleDateString(localeIntl());
};

const routeFil = (fil) =>
  fil.entiteType === 'SINISTRE' ? `/sinistres/${fil.entiteTrackingId}` : '/discussions';

export default function MesDiscussions() {
  const { t } = useTranslation('discussions');
  const navigate = useNavigate();
  const [fils, setFils] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('tous');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await discussionService.mesFils({ page: page - 1, size: TAILLE_PAGE });
      setFils(r.items);
      setTotal(r.total);
    } catch {
      setFils([]);
      setTotal(0);
    } finally {
      setChargement(false);
    }
  }, [page]);

  useEffect(() => { charger(); }, [charger]);

  // Le filtre s'applique à la page reçue et non à la requête : le tri se fait sur la
  // dernière activité côté serveur, et filtrer en base changerait la pagination sous
  // les pieds de l'agent à chaque message qui arrive.
  const visibles = filtre === 'non-lus' ? fils.filter((f) => f.nonLus > 0) : fils;

  return (
    <Layout title={t('titre')}>
      {/* `total` vient du serveur et c'est celui de la pagination : le filtre
          « non lus » ne s'applique qu'a la page recue, il ne peut donc pas
          produire un compte global. */}
      <PageHeader
        titre={t('titre')}
        icone={<MessageOutlined />}
        compte={total}
        unite={t('unite')}
        actions={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Segmented
              size="small"
              value={filtre}
              onChange={setFiltre}
              options={[
                { label: t('filtres.tous'), value: 'tous' },
                { label: t('filtres.nonLus'), value: 'non-lus' },
              ]}
            />
            <Tooltip title={t('actualiser')}>
              <ReloadOutlined
                onClick={charger}
                style={{ cursor: 'pointer', color: C.muted }}
              />
            </Tooltip>
          </div>
        )}
      />

      <div className="cb-card" style={{ padding: 0, overflow: 'hidden' }}>
        {chargement && <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>}

        {!chargement && visibles.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={filtre === 'non-lus'
              ? t('vide.toutLu')
              : t('vide.aucune')}
            style={{ padding: '48px 0' }}
          />
        )}

        {!chargement && visibles.map((f) => (
          <div
            key={`${f.entiteType}-${f.entiteTrackingId}`}
            onClick={() => navigate(routeFil(f))}
            style={{
              display: 'flex', gap: 12, padding: '13px 18px', cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              background: f.nonLus > 0 ? C.greenLight : '#fff',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = f.nonLus > 0 ? C.greenLight : '#fff';
            }}
          >
            <span style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0, marginTop: 2,
              background: couleurAvatar(f.dernierAuteur?.nom || ''), color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
            }}>
              {f.dernierAuteur?.initiales || '?'}
            </span>

            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
                  {f.entiteLibelle || t('dossierSupprime')}
                </span>
                <span style={{ fontSize: 11.5, color: C.faint }}>
                  {tempsRelatif(f.dernierMessageDate)}
                </span>
              </div>
              <div style={{
                fontSize: 12.5, color: C.muted, marginTop: 3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <strong style={{ color: C.text, fontWeight: 600 }}>
                  {[f.dernierAuteur?.prenoms, f.dernierAuteur?.nom].filter(Boolean).join(' ')}
                </strong>
                {' - '}{f.dernierExtrait}
              </div>
            </div>

            {f.nonLus > 0 && (
              <Badge
                count={f.nonLus}
                style={{ background: C.greenMid, marginTop: 6 }}
              />
            )}
            {f.nonLus === 0 && (
              <MessageOutlined style={{ color: C.faint, fontSize: 14, marginTop: 8 }} />
            )}
          </div>
        ))}

        {!chargement && total > TAILLE_PAGE && (
          <div style={{ padding: 14, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              size="small"
              current={page}
              total={total}
              pageSize={TAILLE_PAGE}
              showSizeChanger={false}
              onChange={setPage}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
